"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Button from "../../../components/Button";
import FormInput from "../../../components/FormInput";
import Dropdown from "../../../components/Dropdown";
import styles from "../css/creditContact.module.css";

const CreditContact = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const creditFlow = location?.state?.creditFlow;
  const isReviewMode = creditFlow?.mode === "review";
  const savedData = location?.state?.formData || {};
  const creditStatus = String(creditFlow?.status || "").toLowerCase();
  const isReadOnly = isReviewMode && ["awaiting", "pending", "decision"].includes(creditStatus);

  const steps = useMemo(
    () => [
      { key: "personal", label: "Personal" },
      { key: "contact", label: "Contact" },
      { key: "employment", label: "Employment" },
      { key: "financial", label: "Financial" },
      { key: "consent", label: "Consent" },
    ],
    []
  );

  const provinces = useMemo(
    () => [
      "Eastern Cape",
      "Free State",
      "Gauteng",
      "KwaZulu-Natal",
      "Limpopo",
      "Mpumalanga",
      "Northern Cape",
      "North West",
      "Western Cape",
    ],
    []
  );

  const [form, setForm] = useState({
    mobile: savedData.mobile || "",
    email: savedData.email || "",
    streetAddress: savedData.streetAddress || "",
    suburb: savedData.suburb || "",
    city: savedData.city || "",
    province: savedData.province || "",
    postalCode: savedData.postalCode || "",
  });

  const [emailError, setEmailError] = useState("");

  const validateEmail = (email) => {
    const s = String(email || "").trim();
    if (!s) return "";
    // Keep it simple; still leverages input type=email, but gives a nice message.
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    return ok ? "" : "Please enter a valid email address.";
  };

  const handleChange = (name) => (e) => {
    if (isReadOnly) return;
    let value = e?.target?.value ?? "";

    if (name === "mobile") {
      value = String(value).replace(/\D/g, "").slice(0, 9);
    }

    if (name === "postalCode") {
      value = String(value).replace(/\D/g, "").slice(0, 4);
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmailBlur = () => {
    setEmailError(validateEmail(form.email));
  };

  const handleContinue = () => {
    const err = validateEmail(form.email);
    setEmailError(err);
    if (err) return;

    navigate("/credits/employment", {
      state: {
        creditFlow,
        formData: {
          ...savedData,
          ...form,
        },
      },
    });
  };

  return (
    <div className={styles.page}>
      <Navbar backTo="/" hideBack={isReviewMode} />

      <main className={styles.main}>
        <div className={styles.titleWrap}>
          <h1 className={styles.title}>Credit Application</h1>
          <p className={styles.subtitle}>
            Complete the form below to apply for purchase-on-credit at your pharmacy.
          </p>
        </div>

        <div className={styles.stepper} aria-label="Application steps">
          {steps.map((s, idx) => {
            const isActive = idx === 1;
            const isDone = idx < 1;
            return (
              <div key={s.key} className={styles.stepItem}>
                <div
                  className={`${styles.stepCircle} ${isActive ? styles.stepActive : ""} ${
                    isDone ? styles.stepDone : ""
                  }`}
                >
                  {isDone ? "✓" : idx + 1}
                </div>
                <div className={styles.stepLabel}>{s.label}</div>
                {idx < steps.length - 1 ? <div className={styles.stepLine} aria-hidden /> : null}
              </div>
            );
          })}
        </div>

        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon} aria-hidden>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.86.3 1.7.54 2.51a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.57-1.06a2 2 0 0 1 2.11-.45c.81.24 1.65.42 2.51.54A2 2 0 0 1 22 16.92Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={styles.sectionTitle}>Contact Information</div>
          </div>

          <div className={styles.grid}>
            <FormInput
              label="Mobile Number"
              name="mobile"
              value={form.mobile}
              onChange={handleChange("mobile")}
              placeholder="+27"
              disabled={isReadOnly}
            />

            <div>
              <FormInput
                label="Email Address"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange("email")}
                onBlur={handleEmailBlur}
                placeholder="you@example.com"
                disabled={isReadOnly}
              />
              {emailError ? <small className={styles.errorText}>{emailError}</small> : null}
            </div>

            <FormInput
              label="Street Address"
              name="streetAddress"
              value={form.streetAddress}
              onChange={handleChange("streetAddress")}
              placeholder="123 Main Road"
              disabled={isReadOnly}
            />

            <FormInput
              label="Suburb"
              name="suburb"
              value={form.suburb}
              onChange={handleChange("suburb")}
              placeholder="Suburb"
              disabled={isReadOnly}
            />

            <FormInput
              label="City"
              name="city"
              value={form.city}
              onChange={handleChange("city")}
              placeholder="Johannesburg"
              disabled={isReadOnly}
            />

            <Dropdown
              label="Province"
              name="province"
              options={provinces}
              value={form.province}
              onChange={(value) => {
                if (isReadOnly) return;
                setForm((prev) => ({ ...prev, province: value }));
              }}
              placeholder="Select province"
              disabled={isReadOnly}
            />

            <div className={styles.postalRow}>
              <FormInput
                label="Postal Code"
                name="postalCode"
                value={form.postalCode}
                onChange={handleChange("postalCode")}
                placeholder="2000"
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.actions}>
            <Button
              variant="primary"
              type="button"
              onClick={() =>
                isReviewMode
                  ? navigate("/credits", {
                      state: {
                        creditFlow,
                        formData: {
                          ...savedData,
                          ...form,
                        },
                      },
                    })
                  : navigate(-1)
              }
            >
              Back
            </Button>
            <Button variant="approve" type="button" onClick={handleContinue}>
              Continue
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CreditContact;
